import { routes } from "@bezoned/shared/routes.js";
import { AppEventNames } from "@bezoned/shared/types/app-event-names.js";

import {
  appEventCreate,
  appPublicEventCreate,
} from "../../services/api.service.js";
import { env } from "../../utils/env.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";

let journeyId: string | null = null;
let journeyStartPromise: Promise<string | null> | null = null;

const JOURNEY_ID_STORAGE_KEY = "bezoned:onboarding:journey-id";
const USER_CONSENT_STORAGE_KEY = "bezoned:onboarding:user-consent";
const ADMIN_CONSENT_STORAGE_KEY = "bezoned:onboarding:admin-consent";
export const ONBOARDING_JOURNEY_ID_QUERY_PARAM = "onboardingJourneyId";

const terminalOnboardingEvents = new Set<string>([
  AppEventNames.ConsentUserDenied,
  AppEventNames.ConsentUserError,
  AppEventNames.ConsentAdminDenied,
  AppEventNames.ConsentAdminError,
  AppEventNames.LobbyArrived,
  AppEventNames.OfficeEntered,
]);

const isJourneyId = (value: string | null) =>
  value !== null &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
    value
  );

const getPersistedJourneyId = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const persistedJourneyId = window.sessionStorage.getItem(
      JOURNEY_ID_STORAGE_KEY
    );
    return isJourneyId(persistedJourneyId) ? persistedJourneyId : null;
  } catch {
    return null;
  }
};

const clearOfficeJourneyIdFromUrl = () => {
  if (
    typeof window === "undefined" ||
    window.location.pathname !== routes.protected.office
  ) {
    return;
  }

  const url = new URL(window.location.href);
  if (!url.searchParams.has(ONBOARDING_JOURNEY_ID_QUERY_PARAM)) {
    return;
  }

  url.searchParams.delete(ONBOARDING_JOURNEY_ID_QUERY_PARAM);
  window.history.replaceState(window.history.state, "", url);
};

const consumeOfficeJourneyIdFromUrl = () => {
  if (
    typeof window === "undefined" ||
    window.location.pathname !== routes.protected.office
  ) {
    return null;
  }

  const url = new URL(window.location.href);
  const journeyIdFromUrl = url.searchParams.get(
    ONBOARDING_JOURNEY_ID_QUERY_PARAM
  );
  if (!isJourneyId(journeyIdFromUrl)) {
    return null;
  }

  clearOfficeJourneyIdFromUrl();
  return journeyIdFromUrl;
};

const persistJourneyId = (value: string) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(JOURNEY_ID_STORAGE_KEY, value);
  } catch {
    // Tracking state must never block authentication.
  }
};

const clearPersistedJourneyId = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(JOURNEY_ID_STORAGE_KEY);
  } catch {
    // Tracking state must never block authentication.
  }
};

const hasPendingInteractiveConsent = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return [USER_CONSENT_STORAGE_KEY, ADMIN_CONSENT_STORAGE_KEY].some(
      (key) => window.sessionStorage.getItem(key) === "pending"
    );
  } catch {
    return false;
  }
};

const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

const reportTrackingFailure = (error: unknown, eventName: string) => {
  recordErrorInSentry(toError(error), {
    extra: {
      eventName,
      source: "onboarding-tracking",
    },
  });
};

export const startOnboardingJourney = async (): Promise<string | null> => {
  if (journeyStartPromise) {
    return await journeyStartPromise;
  }

  journeyStartPromise = (async () => {
    try {
      const officeJourneyId = consumeOfficeJourneyIdFromUrl();
      if (officeJourneyId) {
        journeyId = officeJourneyId;
        return journeyId;
      }

      const persistedJourneyId = getPersistedJourneyId();
      if (persistedJourneyId && hasPendingInteractiveConsent()) {
        journeyId = persistedJourneyId;
        return journeyId;
      }

      clearPersistedJourneyId();

      const response = await appPublicEventCreate({
        client: "web",
        event: AppEventNames.AppLoaded,
        version: env.PUBLIC_APPLICATION_VERSION,
      });
      const { journeyId: startedJourneyId } = response;
      journeyId = startedJourneyId;
      return journeyId;
    } catch (error) {
      reportTrackingFailure(error, AppEventNames.AppLoaded);
      return null;
    }
  })();

  return await journeyStartPromise;
};

export const getOnboardingJourneyId = async () =>
  await startOnboardingJourney();

const markConsentPending = async (storageKey: string) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, "pending");
  } catch {
    // Tracking state must never block authentication.
  }

  const currentJourneyId = await startOnboardingJourney();
  if (currentJourneyId) {
    persistJourneyId(currentJourneyId);
  }
};

const consumeConsentPending = (storageKey: string) => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    const isPending = window.sessionStorage.getItem(storageKey) === "pending";
    if (isPending) {
      const persistedJourneyId = getPersistedJourneyId();
      if (persistedJourneyId) {
        journeyId = persistedJourneyId;
      }
    }
    window.sessionStorage.removeItem(storageKey);
    return isPending;
  } catch {
    return false;
  }
};

export const markInteractiveUserConsentPending = () =>
  markConsentPending(USER_CONSENT_STORAGE_KEY);

export const consumeInteractiveUserConsent = () =>
  consumeConsentPending(USER_CONSENT_STORAGE_KEY);

export const markInteractiveAdminConsentPending = () =>
  markConsentPending(ADMIN_CONSENT_STORAGE_KEY);

export const consumeInteractiveAdminConsent = () =>
  consumeConsentPending(ADMIN_CONSENT_STORAGE_KEY);

export const trackPublicOnboardingEvent = (
  eventName: "consent.user.denied" | "consent.user.error"
) => {
  const sendEvent = async () => {
    try {
      const currentJourneyId = journeyId ?? (await startOnboardingJourney());
      clearPersistedJourneyId();
      await appPublicEventCreate({
        client: "web",
        event: eventName,
        journeyId: currentJourneyId ?? undefined,
        version: env.PUBLIC_APPLICATION_VERSION,
      });
    } catch (error) {
      reportTrackingFailure(error, eventName);
    }
  };

  void sendEvent();
};

export const trackAuthenticatedOnboardingEvent = (
  eventName:
    | "consent.user.succeeded"
    | "consent.user.already-granted"
    | "consent.admin.succeeded"
    | "consent.admin.already-granted"
    | "consent.admin.denied"
    | "consent.admin.error"
    | "lobby.arrived"
    | "office.entered"
) => {
  const sendEvent = async () => {
    try {
      const currentJourneyId = journeyId ?? (await startOnboardingJourney());
      if (!currentJourneyId) {
        reportTrackingFailure(
          new Error("Onboarding journey ID was unavailable."),
          eventName
        );
        return;
      }

      if (terminalOnboardingEvents.has(eventName)) {
        // A reload from Lobby or Office starts a separate journey.
        clearPersistedJourneyId();
        if (eventName === AppEventNames.OfficeEntered) {
          clearOfficeJourneyIdFromUrl();
        }
      }

      await appEventCreate({
        client: "web",
        event: eventName,
        journeyId: currentJourneyId,
        version: env.PUBLIC_APPLICATION_VERSION,
      });
    } catch (error) {
      reportTrackingFailure(error, eventName);
    }
  };

  void sendEvent();
};

export const resetOnboardingTrackerForTests = ({
  preserveStorage = false,
}: {
  preserveStorage?: boolean;
} = {}) => {
  journeyId = null;
  journeyStartPromise = null;
  if (preserveStorage) {
    return;
  }

  clearPersistedJourneyId();
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(USER_CONSENT_STORAGE_KEY);
    window.sessionStorage.removeItem(ADMIN_CONSENT_STORAGE_KEY);
  } catch {
    // Test cleanup must tolerate unavailable storage.
  }
};
